/**
 * advisorLandingSession — Storage-Helper für den Einmal-pro-Tab-Session-Redirect
 * des Advisor-Cockpit-First-Landings (M-ACF-1,
 * `architecture-advisor-cockpit-first.md` §3.1 / §7.2 / §8 / §10).
 *
 * Zwei Storage-Marker:
 *  - `faebill.advisorLanding.seen` (sessionStorage, Wert = UID): „Initial-Einstieg
 *    dieser Tab-Session hat stattgefunden." Pro Tab; überlebt F5; stirbt mit dem
 *    Tab; gelöscht bei echtem Credential-Login; uid-Wert verhindert
 *    Account-Carry-over.
 *  - `faebill.advisorLanding.hint.<uid>` (localStorage, Wert = `'1'`): reine
 *    Politur gegen den Formular-Flash — „Nutzer hatte zuletzt eine
 *    advisor-Membership". Self-healing (Clear, wenn Membership wegfällt). NIE
 *    autorisierungsrelevant.
 *
 * ALLE Funktionen sind intern hinter {@link ADVISOR_LANDING_ENABLED} geguarded
 * (Flag off ⇒ no-op, keine Storage-Berührung ⇒ Inert-Merge) UND
 * storage-fehler-tolerant: Storage kann werfen (Safari Private Mode,
 * Enterprise-Policies). Die Fehler-RICHTUNG ist pro Funktion FEST definiert:
 *  - Marker-Read fail-CLOSED für den Redirect (Fehler ⇒ „gesehen" ⇒ kein
 *    Redirect — ein Endlos-Redirect, der den Owner-Modus unerreichbar macht,
 *    wäre der schlimmere Fehler).
 *  - Hint-Read fail-OPEN für den Hold (Fehler ⇒ kein Hold ⇒ schlimmstenfalls
 *    ein kurzer Flash).
 * Keine Exception verlässt die Helper; Fehler gehen ins `logger` (debug-Level,
 * kein User-Toast).
 *
 * Firebase-frei, React-frei (reiner TS + Browser-Storage-API).
 *
 * @module utils/advisorLandingSession
 */

import { ADVISOR_LANDING_ENABLED } from '../config/advisorLandingAccess';
import logger from './logger';

/** sessionStorage-Key für den Session-Einstiegs-Marker (Wert = UID). */
const SEEN_KEY = 'faebill.advisorLanding.seen';

/** localStorage-Key-Präfix für den Membership-Hint (`<präfix><uid>` = `'1'`). */
const HINT_PREFIX = 'faebill.advisorLanding.hint.';

/**
 * Stempelt den Session-Marker auf die übergebene UID. Idempotent. Flag off ⇒
 * no-op (keine Storage-Berührung). Storage-Fehler werden geschluckt (debug-Log).
 *
 * @param uid UID des aktuell eingeloggten Nutzers.
 */
export function markAdvisorLandingSeen(uid: string): void {
  if (!ADVISOR_LANDING_ENABLED) return;
  try {
    sessionStorage.setItem(SEEN_KEY, uid);
  } catch (err) {
    logger.debug({ err }, 'advisorLanding: markSeen failed');
  }
}

/**
 * True, wenn der Einstieg dieser Tab-Session für DIESEN Nutzer bereits
 * stattfand (`stored === uid`). Fail-CLOSED für den Redirect: Flag off ODER
 * Storage-Fehler ⇒ `true` („gesehen" ⇒ kein Redirect).
 *
 * @param uid UID des aktuell eingeloggten Nutzers.
 * @returns `true`, wenn der Marker gesetzt ist (oder Flag off / Storage-Fehler).
 */
export function isAdvisorLandingSeen(uid: string): boolean {
  if (!ADVISOR_LANDING_ENABLED) return true;
  try {
    return sessionStorage.getItem(SEEN_KEY) === uid;
  } catch (err) {
    logger.debug({ err }, 'advisorLanding: isSeen read failed');
    return true;
  }
}

/**
 * Entfernt den Session-Marker (Aufrufer: `LoginPage` nach erfolgreichem
 * Credential-Login — jeder echte Login ist ein frischer Einstieg). Flag off ⇒
 * no-op. Storage-Fehler werden geschluckt (debug-Log).
 */
export function clearAdvisorLandingSeen(): void {
  if (!ADVISOR_LANDING_ENABLED) return;
  try {
    sessionStorage.removeItem(SEEN_KEY);
  } catch (err) {
    logger.debug({ err }, 'advisorLanding: clearSeen failed');
  }
}

/**
 * Setzt den Membership-Hint (`localStorage`, uid-skopiert) — Politur gegen den
 * Formular-Flash: sobald eine advisor-Membership beobachtet wird. Flag off ⇒
 * no-op. Storage-Fehler werden geschluckt (debug-Log).
 *
 * @param uid UID des aktuell eingeloggten Nutzers.
 */
export function setAdvisorMembershipHint(uid: string): void {
  if (!ADVISOR_LANDING_ENABLED) return;
  try {
    localStorage.setItem(HINT_PREFIX + uid, '1');
  } catch (err) {
    logger.debug({ err }, 'advisorLanding: setHint failed');
  }
}

/**
 * True, wenn der Membership-Hint für DIESEN Nutzer gesetzt ist. Fail-OPEN für
 * den Hold: Flag off ODER Storage-Fehler ⇒ `false` (kein Render-Hold ⇒
 * schlimmstenfalls ein kurzer Flash statt eines Crashs).
 *
 * @param uid UID des aktuell eingeloggten Nutzers.
 * @returns `true`, wenn der Hint gesetzt ist (und Flag on, kein Storage-Fehler).
 */
export function hasAdvisorMembershipHint(uid: string): boolean {
  if (!ADVISOR_LANDING_ENABLED) return false;
  try {
    return localStorage.getItem(HINT_PREFIX + uid) === '1';
  } catch (err) {
    logger.debug({ err }, 'advisorLanding: hasHint read failed');
    return false;
  }
}

/**
 * Entfernt den Membership-Hint (self-healing, wenn die geladene Tenant-Liste
 * keine advisor-Membership mehr enthält). Flag off ⇒ no-op. Storage-Fehler
 * werden geschluckt (debug-Log).
 *
 * @param uid UID des aktuell eingeloggten Nutzers.
 */
export function clearAdvisorMembershipHint(uid: string): void {
  if (!ADVISOR_LANDING_ENABLED) return;
  try {
    localStorage.removeItem(HINT_PREFIX + uid);
  } catch (err) {
    logger.debug({ err }, 'advisorLanding: clearHint failed');
  }
}
